"use client";

import { useUser } from "@/components/clients/LocaleToStorage";
import Title from "@/components/ui/title";
import { ValidateFormItem } from "@/components/ui/validate";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import { useSite } from "@/hooks/useSiteInfo";
import { useI18n } from "@/messages/client";
import { getContactFormId, postFormApply } from "@/server/form";
import { isInteger } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import useSWRMutation from "swr/mutation";
import FormSelect from "../Form/FormSelect";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import { PrivacyCheckbox } from "../UserLayout/Register";
import { Button } from "@/components/ui/button";
import FormCaptcha from "../Form/FormCaptcha";
import useLocaleRouter from "@/hooks/useLocaleRouter";

type QuestionType = "experience" | "functional" | "content"

type FeedbackParams = {
  type: string;
  typeName: string;
  link: string;
  description: string;
  email: string;
  code: string;
  privacy: boolean;
}

const questionTypes: {
  value: QuestionType;
  name: string;
  name_en: string;
}[] = [
  {
    value: "experience",
    name: "网站体验问题",
    name_en: "Website experience issues",
  },
  {
    value: "functional",
    name: "网站功能问题",
    name_en: "Website functionality issues",
  },
  {
    value: "content",
    name: "网站内容问题",
    name_en: "Website content issues",
  },
];

function FormLabel(props: {
  label: string;
  htmlForm?: string;
}) {
  return (
    <label className="w-full block mb-1.5 text-sm md:mb-4 md:text-lg" htmlFor={props.htmlForm}>
      {props.label}
    </label>
  )
}

export default function Feedback() {
  const t = useI18n();

  const router = useLocaleRouter();

  const locale = useCurrentLocale();

  const site = useSite();

  const { user, token } = useUser();

  const codeMessage = useCodeMessage();

  const [success, setSuccess] = useState(false);

  const methods = useForm<FeedbackParams>();

  const { handleSubmit, control, setValue } = methods;

  const type = useWatch({
    control,
    name: "type",
  });

  const email = useWatch({
    control,
    name: "email",
  })

  const { trigger, isMutating } = useSWRMutation(
    [
      "/api/form/create",
      site,
      token,
    ],
    async(
      [
        ,
        site,
        token,
      ],
      { arg }: { arg: FeedbackParams }
    ) => {
      const {
        code,
        // eslint-disable-next-line no-unused-vars
        privacy,
        ...formData
      } = arg;
      const formId = await getContactFormId(site, "feedback");
      if (!isInteger(formId)) {
        return formId
      }
      return postFormApply({
        token: token as string,
        formId: formId as number,
        formData,
        code,
      })
    },
    {
      onError: () => {
        toast.error(t("表单.提交失败提示"));
      },
    }
  );

  const onSubmit = handleSubmit(async(data) => {
    try {
      if (!token) {
        return;
      }
      const res = await trigger(data);

      if ("errors" in res) {
        toast.error(
          codeMessage(res.errors[0].extensions?.code, res.errors[0].message),
        );

        return;
      }

      toast.success(t("意见反馈.提交成功提示"), {
        duration: 3000,
      });
      setTimeout(() => {
        router.push('/');
      }, 3000);
      setSuccess(true);
    } catch (error) {
      toast.error(t("表单.提交失败提示"));
    }
  });

  useEffect(() => {
    if (user) {
      setValue("email", user.front_user_email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const questoinLabel = questionTypes.find((el) => el.value === type);
    setValue(
      "typeName",
      (locale === "cn" ? questoinLabel?.name : questoinLabel?.name_en) ?? ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, locale]);

  return (
    <div className="w-full">
      <Title title={t("意见反馈.网站意见反馈")} underline={false} />
      <FormProvider {...methods}>
        <form
          className="w-full flex flex-col gap-y-4 md:gap-y-14"
          onSubmit={onSubmit}
        >
          <Controller
            name="type"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("表单.请选择"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="type">
                <FormLabel label={t("意见反馈.意见类别")} />
                <FormSelect
                  className="md:data-[placeholder]:text-[#E5E5E5]"
                  field={field}
                  placeholder={t("表单.请选择")}
                  options={questionTypes.map((el) => ({
                    value: el.value,
                    label: locale === "cn" ? el.name : el.name_en,
                  }))}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            name="link"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("表单.请输入"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="link">
                <FormLabel label={t("意见反馈.页面链接")} />
                <Input
                  {...field}
                  placeholder={t("表单.请输入")}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            name="description"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("表单.请输入"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="description">
                <FormLabel label={t("意见反馈.问题描述")} />
                <Textarea
                  {...field}
                  className="h-32 py-4 md:h-[360px]"
                  placeholder={t("意见反馈.请输入问题描述")}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("注册模块.请输入邮箱地址"),
              },
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("注册模块.请输入有效的邮箱地址"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="email">
                <FormLabel label={t("意见反馈.邮箱地址")} />
                <Input
                  {...field}
                  type="email"
                  placeholder={t("意见反馈.请输入您的邮箱")}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            name="code"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("找回密码.请输入右侧图形验证码"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem name="code">
                <FormLabel label={t("找回密码.请输入右侧图形验证码")} />
                <FormCaptcha
                  className="mt-0"
                  email={email}
                  value={field.value}
                  onChange={field.onChange}
                />
              </ValidateFormItem>
            )}
          />

          <Controller
            name="privacy"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("注册模块.请阅读并勾选"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem className="mt-6 md:mt-16" name="privacy">
                <PrivacyCheckbox
                  className="text-sm md:text-base"
                  value={field.value}
                  onChange={field.onChange}
                />
              </ValidateFormItem>
            )}
          />

          <Button
            type="submit"
            disabled={success}
            loading={isMutating}
            className="self-center mt-6 rounded-[20px] md:min-w-[180px] md:mt-7 md:h-12 md:text-lg md:rounded-md max-md:w-full"
          >{t("意见反馈.立即提交")}</Button>
        </form>
      </FormProvider>
    </div>
  )
}